import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useFeedback } from '../../hooks/useFeedback';
import { MessageSquare, Check, X } from 'lucide-react';

const FeedbackDialog = ({ show, onHide, auth }) => {
    const [message, setMessage] = useState('');
    const [type, setType] = useState('feature'); // feature, bug, other
    const { submitFeedback, loading, error, success, reset } = useFeedback();

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (show) {
            reset();
            setMessage('');
            setType('feature');
        }
    }, [show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!message.trim()) return;

        await submitFeedback(auth, { message, type });
    };

    // Auto-close on success after a short delay
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => {
                onHide();
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [success, onHide]);

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold d-flex align-items-center gap-2">
                    <div className="bg-primary bg-opacity-10 p-2 rounded-circle">
                        <MessageSquare size={20} className="text-primary" />
                    </div>
                    Give Feedback
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
                {success ? (
                    <div className="text-center py-4">
                        <div className="mb-3">
                            <Check size={48} className="text-success" />
                        </div>
                        <h5>Thank you!</h5>
                        <p className="text-muted">Your feedback has been received.</p>
                    </div>
                ) : (
                    <Form onSubmit={handleSubmit}>
                        {error && (
                            <Alert variant="danger" className="d-flex align-items-center gap-2">
                                <X size={16} /> {error}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium small text-muted text-uppercase">Feedback Type</Form.Label>
                            <div className="d-flex gap-2">
                                {['feature', 'bug', 'other'].map((t) => (
                                    <Button
                                        key={t}
                                        variant={type === t ? 'primary' : 'light'}
                                        size="sm"
                                        className={`flex-grow-1 text-capitalize fw-medium border-0 ${type === t ? 'shadow-sm' : ''}`}
                                        onClick={() => setType(t)}
                                        type="button"
                                    >
                                        {t}
                                    </Button>
                                ))}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-medium small text-muted text-uppercase">Your Message</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Tell us what you think..."
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                required
                                className="bg-light border-0"
                                style={{ resize: 'none' }}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end gap-2 mt-4">
                            <Button variant="light" onClick={onHide} className="fw-bold text-muted">
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading || !message.trim()}
                                className="fw-bold px-4 d-flex align-items-center gap-2"
                            >
                                {loading && <Spinner size="sm" animation="border" />}
                                Submit Feedback
                            </Button>
                        </div>
                    </Form>
                )}
            </Modal.Body>
        </Modal>
    );
};

export default FeedbackDialog;
